// PayU Payment Gateway Configuration

/**
 * PayU Configuration
 * 
 * For production use, please store these credentials in environment variables:
 * - REACT_APP_PAYU_MERCHANT_KEY
 * - REACT_APP_PAYU_MERCHANT_SALT
 * 
 * Never commit actual production credentials to version control.
 */

/**
 * Sanitize and validate PayU URL
 * Removes any unwanted characters or suffixes that might have been appended
 */
const sanitizePayUUrl = (url) => {
  if (!url) return null;
  
  // Trim whitespace and convert to string
  let sanitized = String(url).trim();
  
  // PayU URL pattern - must match exactly to '_payment' endpoint
  const payuPattern = /^(https:\/\/(test|secure)\.payu\.in\/_payment)/;
  
  // Check if URL matches expected PayU URL pattern
  if (payuPattern.test(sanitized)) {
    // Extract just the valid PayU URL part (removes any extra characters/paths)
    const match = sanitized.match(payuPattern);
    if (match) {
      return match[1];
    }
  }
  
  // Return sanitized value even if pattern doesn't match
  // This allows the validation function to catch and report the error
  return sanitized;
};

/**
 * Get callback URLs with runtime window.location
 * Using .html files to handle POST requests from PayU on GitHub Pages
 * GitHub Pages doesn't support POST to routes, only to static HTML files
 */
const getCallbackUrls = () => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  // Determine the base path from PUBLIC_URL environment variable
  // PUBLIC_URL is set by React based on the "homepage" field in package.json
  // For custom domains (e.g., https://extrabyte.in), PUBLIC_URL will be empty or '/'
  // For subdirectory deployments (e.g., https://user.github.io/repo), PUBLIC_URL will be '/repo'
  let basePath = '';
  if (process.env.PUBLIC_URL) {
    const publicUrl = process.env.PUBLIC_URL;
    // Extract path from PUBLIC_URL (e.g., '/repo' from 'https://user.github.io/repo' or '/repo')
    try {
      // Use a dummy base URL for URL parsing - needed when PUBLIC_URL is an absolute URL
      const url = new URL(publicUrl, 'http://localhost');
      basePath = url.pathname === '/' ? '' : url.pathname;
    } catch (e) {
      // If PUBLIC_URL is just a path (e.g., '/repo'), use it directly
      basePath = publicUrl === '/' ? '' : publicUrl;
    }
  }
  
  return {
    successUrl: process.env.REACT_APP_PAYU_SUCCESS_URL || 
      `${baseUrl}${basePath}/payment-success.html`,
    failureUrl: process.env.REACT_APP_PAYU_FAILURE_URL || 
      `${baseUrl}${basePath}/payment-failure.html`,
    cancelUrl: process.env.REACT_APP_PAYU_CANCEL_URL || 
      `${baseUrl}${basePath}/payment-cancel.html`,
  };
};

const PAYU_CONFIG = {
  // Merchant credentials - require environment variables in production
  merchantKey: process.env.REACT_APP_PAYU_MERCHANT_KEY || 
    (process.env.NODE_ENV === 'production' 
      ? (() => { throw new Error('REACT_APP_PAYU_MERCHANT_KEY is required in production'); })()
      : 'gtKFFx'),
  merchantSalt: process.env.REACT_APP_PAYU_MERCHANT_SALT || 
    (process.env.NODE_ENV === 'production'
      ? (() => { throw new Error('REACT_APP_PAYU_MERCHANT_SALT is required in production'); })()
      : 'eCwWELxi'),
  
  // PayU Payment URLs
  // Test URL for development/testing
  // Sanitize the URL to remove any unwanted suffixes (e.g., "npm" appended by mistake)
  paymentUrl: sanitizePayUUrl(process.env.REACT_APP_PAYU_URL) || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://secure.payu.in/_payment'
      : 'https://test.payu.in/_payment'),
};

/**
 * Get PayU configuration
 */
export const getPayUConfig = () => {
  return {
    ...PAYU_CONFIG,
    ...getCallbackUrls()
  };
};

/**
 * Validate PayU configuration
 */
export const validatePayUConfig = () => {
  const requiredFields = ['merchantKey', 'merchantSalt', 'paymentUrl'];
  const missingFields = requiredFields.filter(field => !PAYU_CONFIG[field]);
  
  if (missingFields.length > 0) {
    console.error('Missing PayU configuration fields:', missingFields);
    return false;
  }
  
  // Validate PayU URL format
  const payuUrlPattern = /^https:\/\/(test|secure)\.payu\.in\/_payment$/;
  if (!payuUrlPattern.test(PAYU_CONFIG.paymentUrl)) {
    console.error('Invalid PayU URL format:', PAYU_CONFIG.paymentUrl);
    console.error('Expected format: https://test.payu.in/_payment or https://secure.payu.in/_payment');
    return false;
  }
  
  return true;
};

export default PAYU_CONFIG;
